import { useReducer, useEffect, useRef, useCallback } from 'react';
import { AudioManager } from './audio';
import { PHASE, calculateTotalSeconds } from './constants';

const initialState = {
    phase: PHASE.PREPARE,
    remaining: 0,
    round: 1,
    tabata: 1,
    roundsLeft: 0,
    tabatasLeft: 0,
    running: false,
    totalRemaining: 0,
    roundsTotal: 8,
    tabatasTotal: 4,
    config: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'START': {
            const { config } = action;
            return {
                ...state,
                phase: PHASE.PREPARE,
                remaining: config.prepare,
                round: 1,
                tabata: 1,
                roundsLeft: config.rounds - 1,
                tabatasLeft: config.tabatas - 1,
                running: true,
                totalRemaining: calculateTotalSeconds(config) + config.cooldown,
                roundsTotal: config.rounds,
                tabatasTotal: config.tabatas,
                config,
            };
        }
        case 'PAUSE':
            return { ...state, running: false };
        case 'RESUME':
            return { ...state, running: true };
        case 'STOP':
            return { ...initialState };
        case 'TICK': {
            if (!state.running || state.phase === PHASE.END) return state;

            let {
                phase,
                remaining,
                round,
                tabata,
                roundsLeft,
                tabatasLeft,
                totalRemaining,
                config,
            } = state;

            totalRemaining = Math.max(0, totalRemaining - 1);
            remaining = Math.max(0, remaining - 1);

            if (remaining === 0) {
                // Phase transition
                switch (phase) {
                    case PHASE.PREPARE:
                        phase = PHASE.WORK;
                        remaining = config.work;
                        break;
                    case PHASE.WORK: {
                        if (roundsLeft > 0) {
                            phase = PHASE.REST;
                            remaining = config.rest;
                        } else if (tabatasLeft > 0) {
                            phase = PHASE.REST_BETWEEN;
                            remaining = config.restBetween;
                            tabatasLeft -= 1;
                        } else {
                            if (config.cooldown > 0) {
                                phase = PHASE.COOLDOWN;
                                remaining = config.cooldown;
                            } else {
                                phase = PHASE.END;
                                remaining = 0;
                            }
                        }
                        break;
                    }
                    case PHASE.REST: {
                        if (roundsLeft > 0) {
                            phase = PHASE.WORK;
                            round += 1;
                            roundsLeft -= 1;
                            remaining = config.work;
                        } else if (tabatasLeft > 0) {
                            phase = PHASE.REST_BETWEEN;
                            remaining = config.restBetween;
                            tabatasLeft -= 1;
                        } else {
                            if (config.cooldown > 0) {
                                phase = PHASE.COOLDOWN;
                                remaining = config.cooldown;
                            } else {
                                phase = PHASE.END;
                                remaining = 0;
                            }
                        }
                        break;
                    }
                    case PHASE.REST_BETWEEN:
                        phase = PHASE.WORK;
                        tabata += 1;
                        round = 1;
                        roundsLeft = config.rounds - 1;
                        remaining = config.work;
                        break;
                    case PHASE.COOLDOWN:
                        phase = PHASE.END;
                        remaining = 0;
                        break;
                    default:
                        break;
                }
            }

            return {
                ...state,
                phase,
                remaining,
                round,
                tabata,
                roundsLeft,
                tabatasLeft,
                totalRemaining,
            };
        }
        default:
            return state;
    }
}

export function useTimer() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const audioRef = useRef(new AudioManager(100));
    const intervalRef = useRef(null);
    const wakeLockRef = useRef(null);

    const updateAudioVolume = useCallback((volume) => {
        audioRef.current.setVolume(volume);
    }, []);

    const start = useCallback(async (config) => {
        await audioRef.current.unlock();
        dispatch({ type: 'START', config });
    }, []);

    const pause = useCallback(() => {
        dispatch({ type: 'PAUSE' });
    }, []);

    const resume = useCallback(() => {
        dispatch({ type: 'RESUME' });
    }, []);

    const stop = useCallback(() => {
        dispatch({ type: 'STOP' });
        audioRef.current.stop();
    }, []);

    // Interval tick
    useEffect(() => {
        if (!state.running || state.phase === PHASE.END) return;

        intervalRef.current = setInterval(() => {
            dispatch({ type: 'TICK' });
        }, 1000);

        return () => clearInterval(intervalRef.current);
    }, [state.running, state.phase]);

    // Sound effects on state changes
    useEffect(() => {
        if (!state.config) return;

        const { config, phase, remaining } = state;
        audioRef.current.setVolume(config.volume);

        if (remaining >= 1 && remaining <= 3) {
            audioRef.current.play('countdown', config.sounds.countdown);
        }
    }, [state.remaining, state.config]);

    useEffect(() => {
        if (!state.config) return;

        const { config, phase } = state;
        audioRef.current.setVolume(config.volume);

        switch (phase) {
            case PHASE.WORK:
                audioRef.current.play('work', config.sounds.work);
                break;
            case PHASE.REST:
                audioRef.current.play('rest', config.sounds.rest);
                break;
            case PHASE.REST_BETWEEN:
                audioRef.current.play('restBetween', config.sounds.restBetween);
                break;
            case PHASE.COOLDOWN:
                audioRef.current.play('end', config.sounds.end);
                break;
            case PHASE.END:
                audioRef.current.play('end', config.sounds.end);
                break;
            default:
                break;
        }
    }, [state.phase, state.config]);

    // Wake lock
    useEffect(() => {
        const manageWakeLock = async () => {
            try {
                if (state.running && 'wakeLock' in navigator) {
                    if (!wakeLockRef.current) {
                        wakeLockRef.current = await navigator.wakeLock.request('screen');
                    }
                } else if (wakeLockRef.current) {
                    await wakeLockRef.current.release();
                    wakeLockRef.current = null;
                }
            } catch {
                // ignore
            }
        };

        manageWakeLock();

        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && state.running) {
                manageWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => {});
                wakeLockRef.current = null;
            }
        };
    }, [state.running]);

    return {
        ...state,
        start,
        pause,
        resume,
        stop,
        updateAudioVolume,
    };
}

export default useTimer;
